import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import countryData from '../../../utils/countory.json';
import { validateName, validatePhone, validateDate, validateGender, validateState, validateProfession, validateQualification, validateBio, validateForm } from '../../../utils/validation';
import { getProfile, editProfile, updateProfilePic } from '../../../services/userService';
import { toast } from 'react-toastify';

const DefaultProfile = '/images/profile/default-profile.png';

function PersonalInformation() {

    // Initialize all state
    const [profileData, setProfileData] = useState(null);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        dateJoined: '',
        gender: '',
        country: '',
        state: '',
        zipCode: '',
        profession: '',
        academicQualification: '',
        address: '',
        bio: ''
    });

    const [editFormData, setEditFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        dateJoined: '',
        gender: '',
        country: '',
        state: '',
        zipCode: '',
        profession: '',
        academicQualification: '',
        address: '',
        bio: ''
    });

    // UI state
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [showCountryFieldDropdown, setShowCountryFieldDropdown] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    // Image handling state
    const [profileImage, setProfileImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    // Form state
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState('+374');
    const [searchQuery, setSearchQuery] = useState('');
    const [countrySearchQuery, setCountrySearchQuery] = useState('');

    // Refs
    const dropdownRef = useRef(null);
    const countryDropdownRef = useRef(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await getProfile();
            // console.log('Profile response:', response);
            if (response.success && response.data) {
                const profile = response.data;

                // Update both formData and editFormData with the API response
                const updatedData = {
                    fullName: profile.name || '',
                    email: profile.email || '',
                    phoneNumber: profile.mobile || '',
                    dateOfBirth: profile.date_of_birth?.split('T')[0] || '',
                    dateJoined: profile.date_joined?.split('T')[0] || '',
                    gender: profile.gender?.charAt(0).toUpperCase() + profile.gender?.slice(1) || '',
                    country: profile.country || '',
                    state: profile.state || '',
                    zipCode: profile.zipcode || '',
                    profession: profile.profession || '',
                    academicQualification: profile.qualification || '',
                    address: profile.address || '',
                    bio: profile.bio || ''
                };

                setFormData(updatedData);
                setEditFormData(updatedData);
                setSelectedCountryCode(profile.mobileno_code || '+374');

                // Set profile image if available
                if (profile.profile_pic_url) {
                    setProfileImage(profile.profile_pic_url);
                    setSelectedImage(profile.profile_pic_url);
                    setPreviewImage(profile.profile_pic_url);
                }
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error('Failed to fetch profile data');
        }
    };

    // Filter countries based on search query
    const filteredCountries = countryData.countoryCode.filter(country =>
        country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredCountriesForField = countryData.countoryCode.filter(country =>
        country.country.toLowerCase().includes(countrySearchQuery.toLowerCase())
    );

    // Handle country selection
    const handleCountrySelect = (country) => {
        setSelectedCountryCode(country.code);
        setEditFormData(prevData => ({
            ...prevData,
            country: country.country.split(' ')[0]
        }));
        setShowCountryDropdown(false);
        setSearchQuery('');
    };

    // Handle Click Outside for Country Dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCountryDropdown(false);
            }
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setShowCountryFieldDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Format date for API (keep as YYYY-MM-DD for MySQL)
    const formatDateForAPI = (date) => {
        if (!date) return '';
        return date; // Already in YYYY-MM-DD format from the input
    };

    // Validation rules
    const validationRules = {
        fullName: validateName,
        phoneNumber: validatePhone,
        dateOfBirth: (value) => {
            const dateErrors = [];
            if (!value) {
                dateErrors.push('Date of birth is required');
            } else {
                // Convert YYYY-MM-DD to DD-MM-YYYY for validation
                const [year, month, day] = value.split('-');
                const formattedDate = `${day}-${month}-${year}`;

                // Check if date is valid
                const isValidDate = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])-\d{4}$/.test(formattedDate);
                if (!isValidDate) {
                    dateErrors.push('Date must be in DD-MM-YYYY format (e.g., 02-05-2025)');
                }

                // Check age range
                const birthDate = new Date(year, month - 1, day);
                const today = new Date();
                const age = today.getFullYear() - birthDate.getFullYear();

                if (age < 10) {
                    dateErrors.push('You must be at least 10 years old');
                }
                if (age > 100) {
                    dateErrors.push('Age cannot be more than 100 years');
                }
            }
            return { errors: dateErrors };
        },
        gender: validateGender,
        state: validateState,
        profession: validateProfession,
        academicQualification: validateQualification,
        bio: validateBio
    };

    // Handle opening edit modal
    const handleEditClick = () => {
        setEditFormData({ ...formData });
        // Ensure profile image persists when opening edit modal
        setSelectedImage(profileImage);
        setPreviewImage(profileImage);
        setShowEditModal(true);
    };

    // Handle closing the edit modal
    const handleCloseModal = () => {
        setShowEditModal(false);
        setSearchQuery('');
        setCountrySearchQuery('');
        setShowCountryDropdown(false);
        setShowCountryFieldDropdown(false);
        // Reset form data to original state
        setEditFormData({ ...formData });
        setErrors({});
    };

    // Handle form field changes in the edit modal
    const handleEditChange = (e) => {
        const { id, value } = e.target;
        setEditFormData(prevData => ({
            ...prevData,
            [id]: value
        }));
        // Clear error for the changed field
        if (errors[id]) {
            setErrors(prev => ({
                ...prev,
                [id]: undefined
            }));
        }
    };

    // Handle profile image change
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [id]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // console.log('Form submitted:', formData);
        // Here you would typically send the data to your backend
    };

    // Handle image selection
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Check file size (limit to 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle image upload
    const handleImageUpload = async () => {
        if (!selectedImage) return;

        try {

            // console.log('Selected image:', selectedImage);
            const formData = new FormData();
            formData.append('profileImage', selectedImage);

            // Call the API to upload image
            const response = await updateProfilePic(formData);

            if (response.success) {
                toast.success('Profile image updated successfully!');
                // Refresh profile data to get new image
                await fetchProfile();
                handleImageModalClose();
            } else {
                toast.error(response.message || 'Failed to update profile image');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Failed to upload image. Please try again.');
        }
    };

    // Handle opening image modal
    const handleImageModalOpen = () => {
        setShowImageModal(true);
        // Don't reset the image states here to maintain current image
        setPreviewImage(profileImage); // Show current profile image in modal
    };

    // Handle closing image modal
    const handleImageModalClose = () => {
        setShowImageModal(false);
        // Don't reset selected image if upload was not completed
        if (!selectedImage) {
            setPreviewImage(null);
        }
    };

    // Handle saving the edited profile data
    const handleSaveChanges = async (e) => {
        e.preventDefault();

        const validation = validateForm(editFormData, validationRules);
        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }
        // console.log('Validation:', validation);

        setIsLoading(true);

        try {
            // Prepare the data for API with the exact structure required
            const updateData = {
                profile_pic_url: profileImage || '',
                is_profile_pic_changed: false,
                name: editFormData.fullName || '',
                mobileno_code: selectedCountryCode,
                mobile: editFormData.phoneNumber || '',
                bio: editFormData.bio || '',
                date_of_birth: formatDateForAPI(editFormData.dateOfBirth) || '',
                gender: (editFormData.gender || '').toLowerCase(),
                country: editFormData.country || '',
                state: editFormData.state || '',
                zipcode: editFormData.zipCode || '',
                address: editFormData.address || '',
                language: 'en',
                profession: editFormData.profession || '',
                qualification: editFormData.academicQualification || '',
                experience: ''
            };

            // console.log('Data being sent to API:', updateData);

            // Call the update API
            const response = await editProfile(updateData);
            // console.log('API Response:', response);

            if (response.success) {
                // Update the local state with new data
                setFormData({ ...editFormData });
                toast.success('Profile updated successfully!');
                setShowEditModal(false);

                // Refresh profile data
                await fetchProfile();
            } else {
                toast.error(response.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false);
            setErrors({});
        }
    };

    // Handle country selection for both dropdowns
    const handleCountryCodeSelect = (country) => {
        setSelectedCountryCode(country.code);
        setShowCountryDropdown(false);
        setSearchQuery('');
    };

    const handleCountryFieldSelect = (country) => {
        // Remove country code from country name (e.g., "India (+91)" -> "India")
        const countryName = country.country.split(' (')[0];

        setEditFormData(prevData => ({
            ...prevData,
            country: countryName
        }));
        setShowCountryFieldDropdown(false);
        setCountrySearchQuery('');
    };

    return (
        <div className="p-4">
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - My Profile */}
                <div className="w-full md:w-5/12">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                        <div className="p-4 border-b border-gray-200">
                            <h5 className="text-lg font-semibold">My Profile</h5>
                        </div>
                        <div className="p-4">
                            <div className="text-center mb-4">
                                <div className="mb-3">
                                    <img
                                        src={profileImage || DefaultProfile}
                                        alt="Profile"
                                        className="rounded-full mx-auto"
                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                    />
                                </div>
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 mx-auto w-1/2"
                                        onClick={handleImageModalOpen}
                                    >
                                        <Icon icon="fluent:cloud-arrow-up-24-regular" />
                                        Upload <span className="hidden md:inline">Image</span>
                                    </button>
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <div>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        id="fullName"
                                        value={formData.fullName}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <div>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        id="email"
                                        value={formData.email}
                                        readOnly
                                    />
                                    <div className="text-gray-500 text-sm mt-1">Email cannot be edited</div>
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                <div>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        id="phoneNumber"
                                        value={`${selectedCountryCode} ${formData.phoneNumber}`}
                                        readOnly
                                    />
                                    <div className="text-gray-500 text-sm mt-1">Number cannot be edited</div>
                                </div>
                            </div>

                            {/* <div className="profile-field-group mb-2">
                                <label className="profile-label">Joined Date</label>
                                <div className="profile-field">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="subscriptionDate"
                                        value={formData.dateJoined}
                                        readOnly
                                    />
                                </div>
                                <div className="text-muted small">Date of Joined cannot be edited</div>
                            </div> */}
                        </div>
                    </div>
                </div>

                {/* Right Column - More Information */}
                <div className="w-full md:flex-1">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100 h-full flex flex-col">
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                            <h5 className="text-lg font-semibold text-gray-900">More Information</h5>
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-medium"
                                onClick={handleEditClick}
                            >
                                <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                                Edit
                            </button>
                        </div>
                        <div className="px-4 py-4 sm:px-6 sm:py-5 flex-1">
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                <div>
                                    <dt className="text-gray-500">Gender</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.gender || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Date of Birth</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.dateOfBirth || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Country</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.country || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">State</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.state || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Zip Code</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.zipCode || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Profession</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.profession || '-'}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Academic Qualification</dt>
                                    <dd className="mt-0.5 font-medium text-gray-900">{formData.academicQualification || '-'}</dd>
                                </div>
                            </dl>
                            <div className="mt-5">
                                <dt className="text-sm text-gray-500 mb-1">Address</dt>
                                <dd className="text-sm text-gray-900 whitespace-pre-line border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/60 min-h-[52px]">
                                    {formData.address || '—'}
                                </dd>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Image Upload Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h5 className="text-lg font-semibold">Upload Profile Picture</h5>
                            <button
                                onClick={handleImageModalClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <Icon icon="mdi:close" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="text-center">
                                <div className="mb-4">
                                    {previewImage ? (
                                        <img
                                            src={previewImage}
                                            alt="Preview"
                                            className="rounded-full mx-auto"
                                            style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                                        />
                                    ) : profileImage ? (
                                        <img
                                            src={profileImage}
                                            alt="Current Profile"
                                            className="rounded-full mx-auto"
                                            style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div
                                            className="rounded-full mx-auto flex flex-col items-center justify-center"
                                            style={{
                                                width: '200px',
                                                height: '200px',
                                                backgroundColor: '#f8f9fa'
                                            }}
                                        >
                                            <Icon
                                                icon="bi:image"
                                                style={{ fontSize: '48px', color: '#6c757d' }}
                                            />
                                            <p className="mt-2 mb-0 text-gray-500">No image selected</p>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                    style={{ display: 'none' }}
                                    id="imageInput"
                                />
                                <button
                                    type="button"
                                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 mx-auto"
                                    onClick={() => document.getElementById('imageInput').click()}
                                >
                                    <Icon icon="fluent:cloud-arrow-up-24-regular" />
                                    Choose Image
                                </button>
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                            <button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                                onClick={handleImageModalClose}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleImageUpload}
                                disabled={!selectedImage}
                            >
                                {selectedImage ? (
                                    <>
                                        <Icon icon="fluent:cloud-arrow-up-24-regular" />
                                        Upload Image
                                    </>
                                ) : 'Select an Image'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Profile Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h5 className="text-lg font-semibold">Edit Profile</h5>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <Icon icon="mdi:close" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-4">
                            <form onSubmit={handleSaveChanges}>
                                <div className="grid grid-cols-12 gap-4">
                                    {/* Full Name */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                id="fullName"
                                                value={editFormData.fullName}
                                                onChange={handleEditChange}
                                                placeholder="Enter your full name"
                                                className={`w-full px-3 py-2 border ${errors.fullName ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.fullName && <div className="text-red-500 text-sm mt-1">{errors.fullName[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Email - Read Only */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Email  <span className="text-red-500">*</span></label>
                                            <input
                                                type="email"
                                                value={editFormData.email}
                                                disabled
                                                readOnly
                                                className="w-full px-3 py-2 border border-input-border rounded-lg bg-gray-100"
                                            />
                                            <small className="text-gray-500">Email cannot be edited</small>
                                        </div>
                                    </div>

                                    <div className="col-span-12 mb-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                        <div className="flex flex-col md:flex-row gap-2">
                                            {/* Country Code Selector */}
                                            <div className="relative" ref={dropdownRef}>
                                                <button
                                                    type="button"
                                                    className="px-3 py-2 border border-input-border rounded-lg bg-white flex items-center gap-1"
                                                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                >
                                                    {selectedCountryCode}
                                                    <Icon icon="mdi:chevron-down" />
                                                </button>

                                                {showCountryDropdown && (
                                                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-64 max-h-64 overflow-hidden">
                                                        <div className="p-2 border-b border-gray-200">
                                                            <input
                                                                type="text"
                                                                placeholder="Search country..."
                                                                value={searchQuery}
                                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                                className="w-full px-3 py-2 border border-input-border rounded-lg"
                                                            />
                                                        </div>
                                                        <div className="max-h-48 overflow-y-auto">
                                                            {filteredCountries.map((country, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                                                                    onClick={() => handleCountryCodeSelect(country)}
                                                                >
                                                                    <span>{country.country}</span>
                                                                    <span>{country.code}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Phone Input with Error */}
                                            <div className="flex-1 relative w-full">
                                                <input
                                                    type="tel"
                                                    id="phoneNumber"
                                                    value={editFormData.phoneNumber}
                                                    onChange={handleEditChange}
                                                    placeholder="Enter phone number"
                                                    className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                                />
                                                {errors.phoneNumber && (
                                                    <div className="block text-red-500 text-sm mt-1">
                                                        Phone number must be 5–15 digits
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth  <span className="text-red-500">*</span></label>
                                            <input
                                                type="date"
                                                id="dateOfBirth"
                                                value={editFormData.dateOfBirth}
                                                onChange={handleEditChange}
                                                className={`w-full px-3 py-2 border ${errors.dateOfBirth ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.dateOfBirth && <div className="text-red-500 text-sm mt-1">{errors.dateOfBirth[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Gender */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Gender  <span className="text-red-500">*</span></label>
                                            <select
                                                id="gender"
                                                value={editFormData.gender}
                                                onChange={handleEditChange}
                                                className={`w-full px-3 py-2 border ${errors.gender ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            >
                                                <option value="">Select gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {errors.gender && <div className="text-red-500 text-sm mt-1">{errors.gender[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Country */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Country  <span className="text-red-500">*</span></label>
                                            <div className="relative" ref={countryDropdownRef}>
                                                <input
                                                    type="text"
                                                    value={editFormData.country}
                                                    readOnly
                                                    onClick={() => setShowCountryFieldDropdown(true)}
                                                    placeholder="Select country"
                                                    className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
                                                />
                                                {showCountryFieldDropdown && (
                                                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-full max-h-64 overflow-hidden">
                                                        <div className="p-2 border-b border-gray-200 relative">
                                                            <Icon icon="mdi:magnify" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" width="20" height="20" />
                                                            <input
                                                                type="text"
                                                                placeholder="Search countries..."
                                                                value={countrySearchQuery}
                                                                onChange={(e) => setCountrySearchQuery(e.target.value)}
                                                                className="w-full px-3 py-2 pl-10 border border-input-border rounded-lg"
                                                            />
                                                        </div>
                                                        <div className="max-h-48 overflow-y-auto">
                                                            {filteredCountriesForField.map((country, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                                    onClick={() => handleCountryFieldSelect(country)}
                                                                >
                                                                    <span>{country.country}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* State */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                            <input
                                                type="text"
                                                id="state"
                                                value={editFormData.state}
                                                onChange={handleEditChange}
                                                placeholder="Enter your state"
                                                className={`w-full px-3 py-2 border ${errors.state ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.state && <div className="text-red-500 text-sm mt-1">{errors.state[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Zip Code */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                                            <input
                                                type="text"
                                                id="zipCode"
                                                value={editFormData.zipCode}
                                                onChange={handleEditChange}
                                                placeholder="Enter zip code"
                                                className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                    </div>

                                    {/* Profession */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                                            <input
                                                type="text"
                                                id="profession"
                                                value={editFormData.profession}
                                                onChange={handleEditChange}
                                                placeholder="Enter your profession"
                                                className={`w-full px-3 py-2 border ${errors.profession ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.profession && <div className="text-red-500 text-sm mt-1">{errors.profession[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Academic Qualification */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Academic Qualification</label>
                                            <input
                                                type="text"
                                                id="academicQualification"
                                                value={editFormData.academicQualification}
                                                onChange={handleEditChange}
                                                placeholder="Enter your qualification"
                                                className={`w-full px-3 py-2 border ${errors.academicQualification ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.academicQualification && <div className="text-red-500 text-sm mt-1">{errors.academicQualification[0]}</div>}
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="col-span-12 mb-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                            <textarea
                                                rows={3}
                                                id="address"
                                                value={editFormData.address}
                                                onChange={handleEditChange}
                                                placeholder="Enter your address"
                                                className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                    </div>

                                    {/* Bio */}
                                    <div className="col-span-12 mb-3 hidden">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                                            <textarea
                                                rows={3}
                                                id="bio"
                                                value={editFormData.bio}
                                                onChange={handleEditChange}
                                                placeholder="Tell us about yourself..."
                                                className={`w-full px-3 py-2 border ${errors.bio ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20`}
                                            />
                                            {errors.bio && <div className="text-red-500 text-sm mt-1">{errors.bio[0]}</div>}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                            <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg" onClick={handleCloseModal} disabled={isLoading}>
                                Cancel
                            </button>
                            <button
                                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleSaveChanges}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Saving...
                                    </span>
                                ) : (
                                    'Save Changes'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
}

export default PersonalInformation;